import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { formatUsage } from "./tokens.ts";

/**
 * Cost display, switched on and priced in Admin, Settings. Costs are worked
 * out from token counts and the per-million-token prices entered there, so
 * they are estimates of what Anthropic bills, not the bill itself.
 */
export interface CostDisplay {
  enabled: boolean;
  inputPerMTok: number;
  outputPerMTok: number;
}

type Usage = { inputTokens: number; outputTokens: number };

/** The cost display settings; null while loading. */
export function useCostDisplay(): CostDisplay | null {
  const { data } = useQuery({
    queryKey: ["public-config"],
    queryFn: () => api.get<{ costDisplay?: CostDisplay }>("/public/config"),
    staleTime: 60_000,
  });
  return data?.costDisplay ?? null;
}

export function usageCostUsd(usage: Usage, prices: Pick<CostDisplay, "inputPerMTok" | "outputPerMTok">): number {
  return (usage.inputTokens * prices.inputPerMTok + usage.outputTokens * prices.outputPerMTok) / 1_000_000;
}

/** "$1.23", "$0.042", "$0.0031": enough digits that small calls do not all read as $0.00. */
export function formatUsd(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(4)}`;
}

/** The token line, plus "about $X" when the cost display is switched on. */
export function formatUsageWithCost(usage: Usage | null | undefined, display: CostDisplay | null): string | null {
  const line = formatUsage(usage);
  if (!line || !usage || !display?.enabled) return line;
  return `${line}, about ${formatUsd(usageCostUsd(usage, display))}`;
}
